import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import {
  mdiEmail,
  mdiRefresh,
  mdiLogout,
  mdiDelete,
  mdiClose,
  mdiInbox
} from '@mdi/js';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import { GlassCard } from '../../ui/GlassCard';
import { Button } from '../../ui/Button';
import { Container } from '../../ui/Container';
import { PageHead } from '../../seo';

const PageWrapper = styled.div`
  padding-top: 73px;
  min-height: 100vh;
`;

const Header = styled.header`
  padding: var(--space-6) 0;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: var(--space-8);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-4);
`;

const HeaderTitle = styled.h1`
  font-size: var(--text-2xl);
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);
  margin-bottom: var(--space-8);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(GlassCard)`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: var(--text-3xl);
  font-weight: var(--font-bold);
  color: var(--color-brand-primary);
`;

const StatLabel = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const MessagesSection = styled.section``;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
`;

const SectionTitle = styled.h2`
  font-size: var(--text-xl);
`;

const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const MessageCard = styled(GlassCard)<{ $unread?: boolean }>`
  cursor: pointer;
  border-left: 4px solid ${props => props.$unread ? 'var(--color-brand-primary)' : 'transparent'};

  &:hover {
    transform: translateY(-2px);
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-3);
`;

const MessageMeta = styled.div``;

const MessageFrom = styled.div`
  font-weight: var(--font-semibold);
  margin-bottom: var(--space-1);
`;

const MessageEmail = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const MessageType = styled.span<{ $type: string }>`
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  background: ${props => {
    switch (props.$type) {
      case 'hire': return 'rgba(52, 199, 89, 0.15)';
      case 'app-feedback': return 'rgba(0, 122, 255, 0.15)';
      case 'support': return 'rgba(255, 149, 0, 0.15)';
      default: return 'rgba(81, 20, 97, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'hire': return 'var(--color-success)';
      case 'app-feedback': return '#007aff';
      case 'support': return '#ff9500';
      default: return 'var(--color-brand-primary)';
    }
  }};
`;

const MessageSubject = styled.div`
  font-size: var(--text-base);
  margin-bottom: var(--space-2);
`;

const MessagePreview = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MessageDate = styled.div`
  font-size: var(--text-xs);
  color: var(--color-text-tertiary);
  margin-top: var(--space-3);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--space-12);
  color: var(--color-text-tertiary);
`;

const EmptyIcon = styled.div`
  margin-bottom: var(--space-4);
  opacity: 0.5;
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--space-4);
`;

const ModalContent = styled(GlassCard)`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-6);
`;

const ModalTitle = styled.h3`
  font-size: var(--text-xl);
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  transition: background var(--transition-fast);

  &:hover {
    background: var(--color-border);
  }
`;

const ModalBody = styled.div``;

const DetailRow = styled.div`
  margin-bottom: var(--space-4);
`;

const DetailLabel = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
  margin-bottom: var(--space-1);
`;

const DetailValue = styled.div`
  font-size: var(--text-base);
`;

const MessageBody = styled.div`
  padding: var(--space-4);
  background: var(--color-background-secondary);
  border-radius: var(--radius-md);
  white-space: pre-wrap;
  line-height: var(--leading-relaxed);
`;

const ModalActions = styled.div`
  display: flex;
  gap: var(--space-3);
  margin-top: var(--space-6);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border);
`;

interface Message {
  id: string;
  name: string;
  email: string;
  type: string;
  subject: string;
  message: string;
  createdAt: Timestamp;
  read?: boolean;
}

const typeLabels: Record<string, string> = {
  'general': 'General',
  'hire': 'Hire Us',
  'app-feedback': 'App Feedback',
  'support': 'Support',
};

function formatDate(timestamp: Timestamp): string {
  const date = timestamp.toDate();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function AdminDashboard() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const { logout } = useAuth();

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'contacts'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const msgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Message[];
      setMessages(msgs);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleOpenMessage = async (message: Message) => {
    setSelectedMessage(message);
    if (!message.read) {
      try {
        await updateDoc(doc(db, 'contacts', message.id), { read: true });
        setMessages(msgs => msgs.map(m =>
          m.id === message.id ? { ...m, read: true } : m
        ));
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const handleDelete = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    try {
      await deleteDoc(doc(db, 'contacts', messageId));
      setMessages(msgs => msgs.filter(m => m.id !== messageId));
      setSelectedMessage(null);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const unreadCount = messages.filter(m => !m.read).length;
  const hireCount = messages.filter(m => m.type === 'hire').length;

  return (
    <PageWrapper>
      <PageHead title="Admin Dashboard" description="Internal admin dashboard." noindex />
      <Container>
        <Header>
          <HeaderContent>
            <HeaderTitle>
              <Icon path={mdiInbox} size={1.2} />
              Admin Dashboard
            </HeaderTitle>
            <HeaderActions>
              <Button variant="ghost" size="sm" onClick={fetchMessages}>
                <Icon path={mdiRefresh} size={0.8} />
                Refresh
              </Button>
              <Button as={Link} to="/admin/terra" variant="secondary" size="sm">
                Terra Access
              </Button>
              <Button as={Link} to="/" variant="secondary" size="sm">
                View Site
              </Button>
              <Button variant="ghost" size="sm" onClick={logout}>
                <Icon path={mdiLogout} size={0.8} />
                Logout
              </Button>
            </HeaderActions>
          </HeaderContent>
        </Header>

        <Stats>
          <StatCard>
            <StatValue>{messages.length}</StatValue>
            <StatLabel>Total Messages</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{unreadCount}</StatValue>
            <StatLabel>Unread</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{hireCount}</StatValue>
            <StatLabel>Hire Requests</StatLabel>
          </StatCard>
        </Stats>

        <MessagesSection>
          <SectionHeader>
            <SectionTitle>Messages</SectionTitle>
          </SectionHeader>

          {loading ? (
            <EmptyState>Loading messages...</EmptyState>
          ) : messages.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Icon path={mdiEmail} size={3} />
              </EmptyIcon>
              <p>No messages yet</p>
            </EmptyState>
          ) : (
            <MessageList>
              {messages.map(message => (
                <MessageCard
                  key={message.id}
                  $unread={!message.read}
                  onClick={() => handleOpenMessage(message)}
                >
                  <MessageHeader>
                    <MessageMeta>
                      <MessageFrom>{message.name}</MessageFrom>
                      <MessageEmail>{message.email}</MessageEmail>
                    </MessageMeta>
                    <MessageType $type={message.type}>
                      {typeLabels[message.type] || message.type}
                    </MessageType>
                  </MessageHeader>
                  <MessageSubject>{message.subject}</MessageSubject>
                  <MessagePreview>{message.message}</MessagePreview>
                  {message.createdAt && (
                    <MessageDate>{formatDate(message.createdAt)}</MessageDate>
                  )}
                </MessageCard>
              ))}
            </MessageList>
          )}
        </MessagesSection>
      </Container>

      {selectedMessage && (
        <ModalOverlay onClick={() => setSelectedMessage(null)}>
          <div onClick={(e) => e.stopPropagation()}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{selectedMessage.subject}</ModalTitle>
              <CloseButton onClick={() => setSelectedMessage(null)}>
                <Icon path={mdiClose} size={0.9} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailRow>
                <DetailLabel>From</DetailLabel>
                <DetailValue>{selectedMessage.name} ({selectedMessage.email})</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Type</DetailLabel>
                <DetailValue>
                  <MessageType $type={selectedMessage.type}>
                    {typeLabels[selectedMessage.type] || selectedMessage.type}
                  </MessageType>
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Date</DetailLabel>
                <DetailValue>
                  {selectedMessage.createdAt && formatDate(selectedMessage.createdAt)}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Message</DetailLabel>
                <MessageBody>{selectedMessage.message}</MessageBody>
              </DetailRow>
            </ModalBody>
            <ModalActions>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(selectedMessage.id)}
              >
                <Icon path={mdiDelete} size={0.8} />
                Delete
              </Button>
              <Button
                as="a"
                href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                variant="primary"
                size="sm"
              >
                Reply via Email
              </Button>
            </ModalActions>
          </ModalContent>
          </div>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
}
